import { IntegrationSettingsActions } from "./IntegrationSettingsActions";
import DiscardIcon from "../../../images/portal/trash-simple.svg?react";
import ApiIntegrationIcon from "../../../images/settings/api-access.svg?react";
import PassIcon from "../../../images/check-pass.svg?react";
import FailIcon from "../../../images/check-fail.svg?react";
import CrossIcon from "../../../images/portal/cross-x.svg?react";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
	CoreBody,
	CoreButton,
	CoreCard,
	CoreHeader,
	CoreIcon,
	CoreModal,
	CoreSpinner,
	CoreTextBox,
	coreToast,
	FlexBox,
	FlexBoxVerticalCentered,
	FlexBoxVertical,
	Spacer,
	FlexBoxCentered,
} from "@verity/components/core";
import { Box, IconButton, Link, Skeleton, TextField, Typography } from "@mui/material";
import { useContext, useEffect, useId, useState } from "react";
import apiClient from "../../../helpers/api-client";
import { getLmsName } from "../../../helpers/lms-name";
import i18n from "../../../i18n";
import { initiateAuthenticationRedirect } from "../../../helpers/oauth";
import { PortalControllerContext } from "../../context";

type SVG = typeof PassIcon;

export function LmsApiIntegration({
	instituteId,
	instituteUuid,
	lms,
}: {
	instituteId: number;
	instituteUuid: string;
	lms: string;
}) {
	const { action, apiIntegrations, fetchApiIntegrations } = useContext(PortalControllerContext);
	const hasAccessToken = lms === "moodle" && apiIntegrations?.institute?.edit;
	const [origin, setOrigin] = useState<string>();
	const [courseIdList, setCourseIdList] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	const [apiTestCategory, setApiTestCategory] = useState<string>("");
	const [apiTestModalOpen, setApiTestModalOpen] = useState<boolean>(
		action === "instituteScopeTest" || action === "courseScopeTest",
	);

	// for moodle only
	const [isSaving, setIsSaving] = useState(false);
	const [accessToken, setAccessToken] = useState<string>();
	const [accessTokenDeleted, setAccessTokenDeleted] = useState<boolean>(false);
	const [discardModalOpen, setDiscardModalOpen] = useState(false);
	const [createApiKeyModalOpen, setCreateApiKeyModalOpen] = useState(false);
	const [overrideApiKeyModalOpen, setOverrideApiKeyModalOpen] = useState<number>();

	async function loadData(uuid: string, id: number, lms: string) {
		setIsLoading(true);

		try {
			const instituteOrigin = await apiClient.institute.getAccessControlInfo(uuid).then((x) => x.origin);
			const apiKeysResp = (await apiClient.institute.getApiKeys(uuid)).filter((x) => x.platform === lms);
			const origin = apiKeysResp?.[0]?.origin ?? instituteOrigin ?? undefined;
			setOrigin(origin);

			const courseIds = (await apiClient.course.getInstituteCoursesInfo(id))
				.map((y) => y.courseId)
				.filter((y) => !y.includes("$Canvas"))
				.sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));

			setCourseIdList(courseIds);

			if (courseIdList.length === 0) {
				console.error("No Courses");
			}

			if (lms === "moodle") {
				setAccessToken(undefined);
				setAccessTokenDeleted(false);
			}
		} catch (e) {
			console.error(e);
		}

		setIsLoading(false);
	}

	useEffect(() => {
		setIsLoading(true);
		void loadData(instituteUuid, instituteId, lms);
	}, [instituteUuid, instituteId, lms]);

	if (lms !== "canvas" && lms !== "desire2learn" && lms !== "moodle") {
		return <div>{i18n.t("portal.components.settings.lmsApiIntegrationTSX.lmsUnsupported")}</div>;
	}

	return (
		<Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "20px" }}>
			{lms === "moodle" && (
				<IntegrationSettingsActions
					actions={[
						{
							text: i18n.t("portal.components.settings.lmsApiIntegrationTSX.discardChanges"),
							icon: <DiscardIcon width="22px" />,
							color: "cancel",
							disabled: isSaving || isLoading,
							onClick: () => setDiscardModalOpen(true),
						},
						{
							text: i18n.t("portal.components.settings.lmsApiIntegrationTSX.saveChanges"),
							icon: isSaving ? <CoreSpinner size="16px" /> : <SaveOutlinedIcon width="22px" />,
							disabled: isSaving || isLoading,
							onClick: async () => {
								setIsSaving(true);
								try {
									const proms: Promise<unknown>[] = [];
									if (hasAccessToken && accessTokenDeleted) {
										proms.push(apiClient.lmsInfo.deleteToken());
										fetchApiIntegrations();
									}
									if (accessToken && !accessTokenDeleted) {
										proms.push(apiClient.lmsInfo.createMoodleToken(accessToken));
										fetchApiIntegrations();
									}
									await Promise.all(proms);

									coreToast.success(i18n.t("portal.components.settings.lmsApiIntegrationTSX.saveToastSuccess"));
									setIsSaving(false);

									void loadData(instituteUuid, instituteId, lms);
								} catch (e) {
									coreToast.error(i18n.t("portal.components.settings.lmsApiIntegrationTSX.saveToastError"));
									setIsSaving(false);
								}
							},
						},
					]}
					loading={isLoading}
				/>
			)}
			<CoreCard
				title={
					<FlexBoxCentered
						sx={{
							height: "54px",
							padding: "7px 8px",
							justifyContent: "left",
						}}
					>
						{/*
						 TODO: Make v2 of current core components to correctly allow
						 for child sx overriding & faster frontend development

						 Do not modify current core components - use pattern as in Generics/*
						 to create new boilerplates and use a UI config file (global or scoped)

						 Examples: CoreBody, CoreHeader, Spacer, FlexBox*

						 Material will drop support for non-inline CSS & theme is improperly scoped
						 for use with TSX. Attempt to use sx for styling, and enable correct overriding
						 */}
						{isLoading && <Skeleton variant="text" height="54px" width="30%" />}
						{!isLoading && (
							<>
								<CoreIcon component={ApiIntegrationIcon} sx={{ color: "#42296E" }} />
								<Spacer size={7} />
								<CoreHeader variant="h1">
									{i18n.t("portal.components.settings.lmsApiIntegrationTSX.lmsApiAccess")}
								</CoreHeader>
							</>
						)}
					</FlexBoxCentered>
				}
				useHeader
				style={{
					rowGap: "0px",
					columnGap: "0px",
					padding: "0px !important",
					gap: "0px !important",
					boxShadow: "0px 0px 4px 0px #00000040",
					marginTop: lms === "moodle" ? "0px" : "14px",
				}}
			>
				<FlexBoxVertical sx={{ padding: "20px", gap: "25px" }}>
					{isLoading ? (
						<Skeleton variant="text" />
					) : (
						<CoreBody sx={{ fontSize: "15px", color: "#6B6B6B" }}>
							{i18n.t("portal.components.settings.lmsApiIntegrationTSX.description", { lms: getLmsName(lms) })}
						</CoreBody>
					)}

					<Box display="flex" gap="30px" alignItems="top">
						{isLoading ? (
							<>
								<Box flexBasis="30%">
									<Skeleton variant="text" />
									<Skeleton variant="text" />
								</Box>
								<Skeleton variant="rounded" height="40px" width="100%" style={{ flexBasis: "70%" }} />
							</>
						) : (
							<>
								<FlexBoxVertical noPad sx={{ width: "200px" }}>
									<CoreHeader sx={{ fontSize: "15px" }}>{"Domain"}</CoreHeader>

									<Spacer size={5} />

									<CoreBody sx={{ fontSize: "14px", color: "#757575", lineHeight: "1.6" }}>
										{i18n.t("portal.components.settings.lmsApiIntegrationTSX.baseUrl")}
									</CoreBody>
								</FlexBoxVertical>
								<CoreTextBox
									placeholder={i18n.t("portal.components.settings.lmsApiIntegrationTSX.domainPlaceholder")}
									value={origin ?? ""}
									autoComplete="off"
									fullWidth
									disabled
									height={"47px"}
								/>
							</>
						)}
					</Box>

					<Box display="flex" gap="30px" alignItems="top">
						{isLoading ? (
							<>
								<Box flexBasis="30%">
									<Skeleton variant="text" />
									<Skeleton variant="text" />
								</Box>
								<Skeleton variant="rounded" height="40px" width="100%" style={{ flexBasis: "70%" }} />
							</>
						) : (
							<>
								<FlexBoxVertical noPad sx={{ width: "200px" }}>
									<CoreHeader sx={{ fontSize: "15px" }}>
										{i18n.t("portal.components.settings.lmsApiIntegrationTSX.lmsApiLink")}
									</CoreHeader>

									<Spacer size={5} />

									<CoreBody sx={{ fontSize: "14px", color: "#757575", lineHeight: "1.6" }}>
										{i18n.t("portal.components.settings.lmsApiIntegrationTSX.apiEndpoint")}
									</CoreBody>
								</FlexBoxVertical>
								<CoreTextBox
									placeholder={i18n.t("portal.components.settings.lmsApiIntegrationTSX.apiLinkPlaceholder")}
									value={origin ?? ""}
									autoComplete="off"
									fullWidth
									disabled
									height={"47px"}
								/>
							</>
						)}
					</Box>

					<Box>
						{isLoading ? (
							<>
								<Skeleton variant="text" width="100px" />
								<Skeleton variant="text" />
								<Box display="flex" gap="12px" margin="12px 0">
									<Skeleton variant="rounded" height="32px" width="120px" />
									<Skeleton variant="rounded" height="32px" width="120px" />
								</Box>
							</>
						) : (
							<>
								<FlexBoxVertical
									noPad
									sx={{
										gap: lms === "moodle" && !((accessToken || hasAccessToken) && !accessTokenDeleted) ? "2px" : "15px",
									}}
								>
									<FlexBoxVertical noPad sx={{ gap: "5px" }}>
										<CoreHeader sx={{ fontSize: "15px" }}>
											{i18n.t("portal.components.settings.lmsApiIntegrationTSX.accessKeys")}
										</CoreHeader>

										<CoreBody sx={{ fontSize: "14px", color: "#757575", lineHeight: "1.6" }}>
											{i18n.t("portal.components.settings.lmsApiIntegrationTSX.keysDescription")}
										</CoreBody>
									</FlexBoxVertical>

									{lms === "moodle" ? (
										<Box display="flex" flexDirection="column" gap="8px">
											{(accessToken || hasAccessToken) && !accessTokenDeleted ? (
												<Box display="flex" alignItems="center" gap="10px" flexWrap="wrap">
													<CoreHeader
														sx={{
															fontSize: "15px",
															whiteSpace: "nowrap",
														}}
													>
														{i18n.t("portal.components.settings.lmsApiIntegrationTSX.accessKeyLabel")}
													</CoreHeader>
													<Box display="flex" gap="10px" flexWrap="wrap">
														<CoreButton onClick={() => setOverrideApiKeyModalOpen(0)} disabled={isSaving}>
															{i18n.t("portal.components.settings.lmsApiIntegrationTSX.override")}
														</CoreButton>
														<CoreButton
															aria-haspopup="dialog"
															onClick={() => {
																setApiTestModalOpen(true);
																setApiTestCategory("institute");
															}}
															disabled={isSaving && !accessToken}
														>
															{i18n.t("portal.components.settings.lmsApiIntegrationTSX.testInstitutionAccess")}
														</CoreButton>
														<CoreButton
															aria-haspopup="dialog"
															onClick={() => {
																setApiTestModalOpen(true);
																setApiTestCategory("course");
															}}
															disabled={isSaving && !accessToken}
														>
															{i18n.t("portal.components.settings.lmsApiIntegrationTSX.testCourseAccess")}
														</CoreButton>
													</Box>
													<IconButton
														sx={{ padding: "0" }}
														disableTouchRipple
														onClick={() => {
															setAccessTokenDeleted(true);
														}}
														disabled={isSaving}
													>
														<CrossIcon />
													</IconButton>
												</Box>
											) : (
												<CoreBody sx={{ fontSize: "14px", color: "#757575", lineHeight: "1.6" }}>
													{i18n.t("portal.components.settings.lmsApiIntegrationTSX.noAccessKeys")}
												</CoreBody>
											)}
											{!((accessToken || hasAccessToken) && !accessTokenDeleted) && (
												<CoreButton
													style={{ width: "fit-content" }}
													onClick={() => setCreateApiKeyModalOpen(true)}
													disabled={isSaving}
													aria-haspopup="dialog"
												>
													{i18n.t("portal.components.settings.lmsApiIntegrationTSX.addAccessKey")}
												</CoreButton>
											)}
										</Box>
									) : (
										<Box display="flex" flexDirection="column" gap="8px">
											<Box display="flex" alignItems="center" gap="10px" flexWrap="wrap">
												<CoreButton
													aria-haspopup="dialog"
													onClick={() => {
														setApiTestModalOpen(true);
														setApiTestCategory("institute");
													}}
												>
													{i18n.t("portal.components.settings.lmsApiIntegrationTSX.testInstitutionAccess")}
												</CoreButton>
												<CoreButton
													aria-haspopup="dialog"
													onClick={() => {
														setApiTestModalOpen(true);
														setApiTestCategory("course");
													}}
												>
													{i18n.t("portal.components.settings.lmsApiIntegrationTSX.testCourseAccess")}
												</CoreButton>
											</Box>
										</Box>
									)}
								</FlexBoxVertical>
							</>
						)}
					</Box>
				</FlexBoxVertical>
			</CoreCard>
			<CoreModal
				open={discardModalOpen}
				title={i18n.t("portal.components.settings.lmsApiIntegrationTSX.discardChanges")}
				onClose={() => setDiscardModalOpen(false)}
				minWidth="min(90%, 460px)"
				padding="30px 20px 20px 20px"
				buttons={[
					{
						text: i18n.t("portal.components.settings.lmsApiIntegrationTSX.cancel"),
						color: "secondary",
						onClick: () => setDiscardModalOpen(false),
					},
					{
						text: i18n.t("portal.components.settings.lmsApiIntegrationTSX.discardChanges"),
						color: "primary",
						onClick: () => {
							setDiscardModalOpen(false);
							loadData(instituteUuid, instituteId, lms)
								.then(() =>
									coreToast.success(i18n.t("portal.components.settings.lmsApiIntegrationTSX.discardToastSuccess")),
								)
								.catch(() =>
									coreToast.error(i18n.t("portal.components.settings.lmsApiIntegrationTSX.discardToastError")),
								);
						},
					},
				]}
			>
				<div style={{ fontSize: "15px" }}>
					{i18n.t("portal.components.settings.lmsApiIntegrationTSX.discardConfirm")}
				</div>
			</CoreModal>
			{createApiKeyModalOpen && (
				<CreateApiKeyModal
					open={createApiKeyModalOpen}
					setOpen={(open) => !open && setCreateApiKeyModalOpen(false)}
					saveButtonOnClick={(a) => {
						setAccessToken(a);
						setAccessTokenDeleted(false);
					}}
					guideLink={"https://support.yuja.com/hc/en-us/articles/28455565329431-Integrating-the-Moodle-API-with-Verity"}
					lms={lms}
				/>
			)}
			{}
			{typeof overrideApiKeyModalOpen === "number" && (
				<CreateApiKeyModal
					open={typeof overrideApiKeyModalOpen === "number"}
					setOpen={(open) => !open && setOverrideApiKeyModalOpen(undefined)}
					saveButtonOnClick={(a) => {
						setAccessToken(a);
						setAccessTokenDeleted(false);
					}}
					guideLink={"https://support.yuja.com/hc/en-us/articles/28455565329431-Integrating-the-Moodle-API-with-Verity"}
					override
					lms={lms}
				/>
			)}
			{apiTestModalOpen && (
				<TestModal
					open={apiTestModalOpen}
					onClose={() => {
						setApiTestModalOpen(false);
					}}
					lms={lms}
					category={apiTestCategory}
					accessToken={accessToken}
				/>
			)}
		</Box>
	);
}

function CreateApiKeyModal({
	open,
	setOpen,
	saveButtonOnClick,
	guideLink,
	initialKeyId,
	override,
	lms,
}: {
	open: boolean;
	setOpen: (open: boolean) => void;
	saveButtonOnClick: (accessToken: string) => void;
	guideLink: string;
	initialKeyId?: string;
	override?: boolean;
	lms: "canvas" | "moodle" | "desire2learn";
}) {
	const [accessToken, setAccessToken] = useState<string | undefined>(initialKeyId);
	return (
		<CoreModal
			open={open}
			title={
				override
					? i18n.t("portal.components.settings.lmsApiIntegrationTSX.overrideAccessKey")
					: i18n.t("portal.components.settings.lmsApiIntegrationTSX.addAccessKey")
			}
			onClose={() => setOpen(false)}
			buttons={[
				{
					text: i18n.t("portal.components.settings.lmsApiIntegrationTSX.cancel"),
					color: "secondary",
					onClick: () => setOpen(false),
				},
				{
					text: override
						? i18n.t("portal.components.settings.lmsApiIntegrationTSX.override")
						: i18n.t("portal.components.settings.lmsApiIntegrationTSX.add"),
					disabled: !accessToken || !/^[a-zA-Z0-9:_-]+$/.test(accessToken),
					onClick: () => {
						if (accessToken) {
							saveButtonOnClick(accessToken);
							setOpen(false);
						}
					},
				},
			]}
			minWidth="min(80%, 600px)"
			maxWidth="min(80%, 600px)"
		>
			<div style={{ fontSize: "15px" }}>
				{(lms === "canvas" || lms === "moodle") &&
					i18n.t("portal.components.settings.lmsApiIntegrationTSX.guideHeader")}
				{lms === "desire2learn" && i18n.t("portal.components.settings.lmsApiIntegrationTSX.guideD2LHeader")}
				<Link href={guideLink} color="#42296e" target="_blank" rel="noopener noreferrer">
					{i18n.t("portal.components.settings.lmsApiIntegrationTSX.generatingAccessToken", { lms: getLmsName(lms) })}
				</Link>
			</div>
			<div style={{ marginTop: "18px", fontSize: "15px" }}>
				{lms === "moodle" && (
					<div>
						<label htmlFor="accessToken">
							<Typography fontFamily="inherit" color="#000" fontWeight="700">
								{i18n.t("portal.components.settings.lmsApiIntegrationTSX.accessKey")}
							</Typography>
							{i18n.t("portal.components.settings.lmsApiIntegrationTSX.institutionAccessKey")}
						</label>
						<TextField
							placeholder={i18n.t("portal.components.settings.lmsApiIntegrationTSX.enterAccessKey")}
							value={accessToken}
							onChange={(e) => setAccessToken(e.target.value)}
							autoComplete="off"
							fullWidth
							inputProps={{ style: { padding: "9px 12px" } }}
							sx={{
								margin: "4px 0",
								"&&& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0b6f85" },
							}}
							id="accessToken"
						/>
						<Box display="flex" flexDirection="column" gap="6px">
							<div style={{ display: "flex", gap: "6px", lineHeight: "normal" }}>
								<Box width="20px" height="20px">
									{accessToken ? (
										<PassIcon width="17px" height="17px" viewBox="-3 -3 32 34" />
									) : (
										<FailIcon width="17px" height="17px" viewBox="0 0 32 34" />
									)}
								</Box>
								{i18n.t("portal.components.settings.lmsApiIntegrationTSX.accessKeyEmpty")}
							</div>
							<div style={{ display: "flex", gap: "6px", lineHeight: "normal" }}>
								<Box width="20px" height="20px">
									{accessToken && /^[a-zA-Z0-9:_-]+$/.test(accessToken) ? (
										<PassIcon width="17px" height="17px" viewBox="-3 -3 32 34" />
									) : (
										<FailIcon width="17px" height="17px" viewBox="0 0 32 34" />
									)}
								</Box>
								{i18n.t("portal.components.settings.lmsApiIntegrationTSX.accessKeyValidation")}
							</div>
						</Box>
					</div>
				)}
			</div>
		</CoreModal>
	);
}

/*
 * Type for the test response
 */
type ScopeTestResponse = {
	key: string;
	relativePath: string;
	requestType: string;
	status: string;
};

/* Modal to request and view api tests */
type TestModalProps = {
	open: boolean;
	onClose: () => void;
	lms: string;
	category: string;
	accessToken?: string;
};

const TestModal = ({ open, onClose, lms, category, accessToken }: TestModalProps) => {
	const { portalContext, action, setAction } = useContext(PortalControllerContext);
	const [testResults, setTestResults] = useState<ScopeTestResponse[]>([]);
	const [testProcessing, setTestProcessing] = useState<boolean>(false);
	const [lmsCourseId, setLmsCourseId] = useState<string | null>(portalContext.lmsProvidedCourseId ?? null);

	useEffect(() => {
		if (!setAction) return;
		if (action === "instituteScopeTest") {
			setTestProcessing(true);
			RequestApiTests("institute")
				.then(() => {
					setAction(undefined);
				})
				.catch((e) => {
					console.error(e);
					setAction(undefined);
				});
		}
		if (action === "courseScopeTest") {
			setTestProcessing(true);
			RequestApiTests("course")
				.then(() => {
					setAction(undefined);
				})
				.catch((e) => {
					console.error(e);
					setAction(undefined);
				});
		}
	}, [action]);

	const RequestApiTests = async (category: string) => {
		setTestProcessing(true);
		if (testProcessing) {
			coreToast.info(i18n.t("scopeTestFramework.errors.alreadyQueued"));
			return;
		}
		/* Attempt to generate API Token */
		if (lms !== "moodle" && portalContext.courseId) {
			try {
				const instituteInfo = await apiClient.lmsInfo.getInstituteLmsInfo();
				if (!instituteInfo?.origin) {
					return { success: false, error: "Domain is missing" };
				} else if (!instituteInfo?.lmsApiClientId || instituteInfo.lmsApiClientId.trim() === "") {
					return { success: false, error: "Invalid key, client ID required" };
				}
				const oauthTokenExists = await apiClient.oauth.refreshStoredToken();
				if (!oauthTokenExists) {
					localStorage.setItem(
						"initialPortalState",
						JSON.stringify({
							action: category + "ScopeTest",
						}),
					);
					await initiateAuthenticationRedirect({
						courseId: portalContext.courseId,
						callbackUrl: window.location.href,
					});
					return;
				}
			} catch (e) {
				console.error(i18n.t("scopeTestFramework.errors.tokenError"), e);
			}
		}

		/* Verify existence of origin and initiate scope tests */
		await apiClient.lmsInfo
			.testToken({
				lms: lms,
				category: category,
				lmsProvidedCourseId: lmsCourseId,
				...(accessToken && { accessToken: accessToken }),
			})
			.then((data) => {
				setTestResults(data);
			})
			.catch((e) => {
				coreToast.info(i18n.t("scopeTestFramework.errors.pipelineError"));
				console.error(e);
				onClose();
			});
	};

	return (
		<CoreModal
			open={open}
			title={i18n.t("scopeTestFramework.components.modal.title")}
			onClose={onClose}
			buttons={[
				{
					text: i18n.t("scopeTestFramework.components.modal.buttons.close"),
					color: "secondary",
					onClick: () => {
						onClose();
					},
				},
			]}
			widthFixed
			minWidth="720px"
			padding={"20px"}
		>
			<FlexBoxVertical noPad sx={{ gap: "8px" }}>
				{/* Title */}
				<CoreBody>{i18n.t("scopeTestFramework.components.modal.information")}</CoreBody>

				{/* Content */}
				<FlexBoxVerticalCentered noPad>
					{testResults.length > 0 ? (
						<>
							{testResults.map((testResult, index) => (
								<TestRow
									key={index.toString()}
									testKey={testResult.key}
									icon={testResult.status === "SUCCESS" ? PassIcon : FailIcon}
								/>
							))}
						</>
					) : testProcessing ? (
						<>
							<CoreSpinner size="100px" />
						</>
					) : (
						<>
							<ApiTestInitiator
								lmsCourseId={lmsCourseId}
								setLmsCourseId={setLmsCourseId}
								requestApiTests={RequestApiTests}
								testCategory={category}
							/>
						</>
					)}
				</FlexBoxVerticalCentered>
			</FlexBoxVertical>
		</CoreModal>
	);
};

/* Api test initiator section */
type ApiTestInitiatorProps = {
	lmsCourseId: string | null;
	setLmsCourseId: (id: string) => void;
	requestApiTests: (category: string) => void;
	testCategory: string;
};

const ApiTestInitiator = (props: ApiTestInitiatorProps) => {
	const { lmsCourseId, setLmsCourseId, requestApiTests, testCategory } = props;

	const [needAdditionalParameters, setNeedAdditionalParameters] = useState<boolean>(true);
	const [parametersReady, setParametersReady] = useState<boolean>(false);
	const headerId = useId();

	useEffect(() => {
		switch (testCategory) {
			case "institute":
				setNeedAdditionalParameters(false);
				break;
			case "course":
				setNeedAdditionalParameters(true);
				break;
		}
	}, []);

	useEffect(() => {
		if (!needAdditionalParameters || (needAdditionalParameters && parametersReady)) {
			requestApiTests(testCategory);
		}
	}, [parametersReady, needAdditionalParameters]);

	return (
		<FlexBox noPad>
			{needAdditionalParameters && testCategory === "course" && (
				<>
					<FlexBoxVertical noPad sx={{ gap: "10px" }}>
						<CoreHeader id={headerId}>
							{i18n.t("scopeTestFramework.components.modal.initiatorCourseIdHeader")}
						</CoreHeader>
						<FlexBox noPad sx={{ gap: "6px" }}>
							<CoreTextBox
								type="text"
								name={i18n.t("portal.components.settings.lmsApiIntegrationTSX.courseIDTitle")}
								height={"47px"}
								width={"100%"}
								margin="none"
								value={lmsCourseId}
								placeholder={i18n.t("portal.components.settings.lmsApiIntegrationTSX.courseIDDesc")}
								onChange={(event) => setLmsCourseId(event.target.value)}
								inputProps={{ "aria-labelledby": headerId }}
							/>
							<CoreButton
								sx={{ height: "auto", margin: "2px", boxSizing: "border-box", textWrap: "noWrap" }}
								onClick={() => setParametersReady(true)}
							>
								{i18n.t("scopeTestFramework.components.modal.buttons.requestTests")}
							</CoreButton>
						</FlexBox>
					</FlexBoxVertical>
				</>
			)}
		</FlexBox>
	);
};

/* Information corresponding to ONE test */
type TestRowProps = {
	icon: SVG;
	testKey: string;
};

const TestRow = (props: TestRowProps) => {
	const { icon, testKey } = props;

	const testTranslations: {
		key: string;
		title: string;
		description: string;
	}[] = i18n.t("scopeTestFramework.tests", { returnObjects: true });

	const translatedTest = testTranslations.find((test) => test.key === testKey);

	const title = translatedTest ? translatedTest.title : i18n.t("scopeTestFramework.defaults.testTitle");
	const description = translatedTest
		? translatedTest.description
		: i18n.t("scopeTestFramework.defaults.testDescription");

	return (
		<FlexBox noPad>
			<CoreIcon sx={{ width: "28px", height: "auto" }} component={icon} />
			<FlexBoxVerticalCentered>
				<FlexBox noPad>
					<CoreHeader>{title}</CoreHeader>
				</FlexBox>
				<FlexBox noPad>
					<CoreBody>{description}</CoreBody>
				</FlexBox>
			</FlexBoxVerticalCentered>
		</FlexBox>
	);
};
